import { useNavigate } from 'react-router-dom';
import type { User } from '../../types/admin';
import type { ColumnDef } from './ColumnVisibilityMenu';

export const USER_COLUMNS: ColumnDef[] = [
  { key: 'PHONE', label: 'Phone' },
  { key: 'ACCOUNT', label: 'Account' },
  { key: 'LOGINS', label: 'Logins' },
  { key: 'ROLE', label: 'Role' },
  { key: 'ENABLED', label: 'Enabled' },
  { key: 'GROUP_COUNT', label: '# of Groups' },
  { key: 'NOTES', label: 'Notes' },
];

export const USER_COLUMNS_DEFAULT_HIDDEN = ['ENABLED'];

interface UserTableProps {
  users: User[];
  hiddenColumns?: Set<string>;
}

export default function UserTable({ users, hiddenColumns }: UserTableProps) {
  const navigate = useNavigate();
  const isVisible = (key: string) => !hiddenColumns?.has(key);

  const handleRowClick = (userId: number) => {
    navigate(`/admin/users/${userId}/edit`);
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Email</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">First Name</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Last Name</th>
            {isVisible('PHONE') && (
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Phone</th>
            )}
            {isVisible('ACCOUNT') && (
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Account</th>
            )}
            {isVisible('LOGINS') && (
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Logins</th>
            )}
            {isVisible('ROLE') && (
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Role</th>
            )}
            {isVisible('ENABLED') && (
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Enabled</th>
            )}
            {isVisible('GROUP_COUNT') && (
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase"># of Groups</th>
            )}
            {isVisible('NOTES') && (
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Notes</th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {users.map((user) => (
            <tr
              key={user.userId}
              onClick={() => handleRowClick(user.userId)}
              className="hover:bg-blue-50 cursor-pointer transition-colors duration-150"
            >
              <td className="px-4 py-2">{user.email}</td>
              <td className="px-4 py-2 whitespace-nowrap">{user.firstName}</td>
              <td className="px-4 py-2 whitespace-nowrap">{user.lastName}</td>
              {isVisible('PHONE') && (
                <td className="px-4 py-2 whitespace-nowrap">{user.mobileNumber || '-'}</td>
              )}
              {isVisible('ACCOUNT') && (
                <td className="px-4 py-2 whitespace-nowrap">{user.accountId || '-'}</td>
              )}
              {isVisible('LOGINS') && (
                <td className="px-4 py-2 whitespace-nowrap">{user.logins}</td>
              )}
              {isVisible('ROLE') && (
                <td className="px-4 py-2 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded text-xs ${
                    user.role === 'admin'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {user.role}
                  </span>
                </td>
              )}
              {isVisible('ENABLED') && (
                <td className="px-4 py-2 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded text-xs ${
                    user.enabled
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {user.enabled ? 'Yes' : 'No'}
                  </span>
                </td>
              )}
              {isVisible('GROUP_COUNT') && (
                <td className="px-4 py-2 whitespace-nowrap">{user.groupCount}</td>
              )}
              {isVisible('NOTES') && (
                <td className="px-4 py-2 max-w-xs truncate" title={user.description || '-'}>
                  {user.description || '-'}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
